import { useQuery } from '@tanstack/react-query';
import React from 'react'
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, CartesianGrid } from 'recharts';
import HTTPClient from 'services/HTTPClient';
import Spinner from './Spinner';

type Props = {
    scores: object
}

const useSummary = () => {
    const url = `/api/quiz-session/summary`
    return useQuery(['performanceSummary'], async () => {
        let data: any = []
        let weeks = await HTTPClient.get().getJSONResult(url)
        weeks = Object.values(weeks)
        for (let i = 0; i < weeks.length; i++) {
            const [score, total] = weeks[i];
            data.push({
                score,
                total,
                percent: total ? score / total : 0,
            })
        }
        return [...data]
    });
};

function getPerformanceMessage(weeklyPercentages) {
    const trend = calculateTrend(weeklyPercentages);
    switch (trend) {
        case "increasing":
            return "Great job! Your performance is steadily improving week by week.";
        case "decreasing":
            return "Your performance hasn't been your best this time. Let's work together to improve for the next week.";
        case "fluctuating":
            return "Your performance has been inconsistent. Let's focus on maintaining a more stable performance.";
        case "high":
            return "Congratulations! Your performance has been consistently outstanding.";
        case "low":
            return "Your performance has been consistently below expectations. Let's work together to improve.";
        default:
            return "Unable to determine performance. Keep up the good work!";
    }
}

function calculateTrend(summary) {
    let weeklyPercentages = summary.map(s => s.total ? (s.score / s.total) * 100 : 0)
    const differences = weeklyPercentages.map((percentage, index) => {
        if (index === 0) return 0;
        return percentage - weeklyPercentages[index - 1];
    });

    const positiveDifferences = differences.filter((diff) => diff > 0);
    const negativeDifferences = differences.filter((diff) => diff < 0);
    console.log("differences", differences, positiveDifferences, negativeDifferences)
    if (positiveDifferences.length === differences.length) return "increasing";
    if (negativeDifferences.length === differences.length) return "decreasing";
    if (positiveDifferences.length > 0 && negativeDifferences.length > 0) return "fluctuating";
    if (weeklyPercentages.every((percentage) => percentage >= 80)) return "high";
    if (weeklyPercentages.every((percentage) => (percentage < 50 || percentage === 0))) return "low";
    let firstScore = weeklyPercentages.at(0)
    let lastScore = weeklyPercentages.at(-1)
    if (firstScore < lastScore) {
        return "increasing";
    } else if (firstScore > lastScore) {
        return "decreasing";
    } else if (lastScore < 40) {
        return "low";
    }
    return "unknown";
}



export default function PerformanceCard({ scores }: Props) {
    const { data: summary, isLoading, error } = useSummary();

    if (isLoading) {
        return <Spinner className='mt-2' />
    }

    const marks = Object.entries(scores).map(([topic, score]) => ({ topic, score }))
    console.log(marks)
    console.log(summary, isLoading, error)

    return (
        <div className="columns performance-card card has-background-dark between px-4 py-5 mx-0">
            <div className='column mx-0'>
                <h5>
                    <strong className='has-text-white-ter'>
                        Performance Overview
                    </strong>
                </h5>
                <p className='has-text-grey-lighter'>
                    {getPerformanceMessage(summary)}
                </p>
            </div>
            <div className="column mx-0">
                <ResponsiveContainer width="95%" height={80} >
                    <LineChart data={summary}>
                        <Line
                            type="monotone"
                            dataKey="percent"
                            stroke="#fff"
                            strokeWidth={3}
                            dot={false}
                        />
                        <CartesianGrid strokeDasharray="4 4" />
                        <XAxis dataKey="name" hide />
                        <YAxis tick={false} hide />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div className="column mx-0">
                <ResponsiveContainer width="100%" height={120}>
                    <RadarChart cx="50%" cy="50%" outerRadius="80%" data={marks}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="topic" />
                        <PolarRadiusAxis tick={false} domain={[0, 100]} />
                        <Radar
                            dataKey="score"
                            stroke="#fff"
                            fill="#fff"
                            fillOpacity={0.6}
                        />
                    </RadarChart>
                </ResponsiveContainer>
            </div>
        </div>
    )
}